import styled from 'styled-components';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import { Container } from '../components/ui/Container';
import SEO from '../components/seo/SEO';
import { useRegionalSeo } from '../hooks/useRegionalSeo';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Main = styled.main`
  ${Container} {
    padding-top: 2rem;
    padding-bottom: 2rem;
    
    @media (min-width: 768px) {
      padding-top: 3rem;
      padding-bottom: 3rem;
    }
    
    @media (min-width: 1024px) {
      padding-top: 4rem;
      padding-bottom: 4rem;
    }
  }
`;

const Content = styled.div`
  max-width: 48rem;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 2rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3rem;
  }
`;

const LastUpdated = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.mutedForeground};
  margin-bottom: 2rem;
  
  @media (min-width: 1024px) {
    font-size: 0.9375rem;
    margin-bottom: 3rem;
  }
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
  
  @media (min-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.5rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 1.25rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.foreground};
  line-height: 1.75;
  margin-bottom: 1.25rem;
  
  @media (min-width: 768px) {
    font-size: 1.0625rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.125rem;
    margin-bottom: 1.5rem;
  }
`;

const List = styled.ul`
  font-size: 1rem;
  color: ${props => props.theme.colors.foreground};
  line-height: 1.75;
  margin-bottom: 1.25rem;
  padding-left: 1.5rem;
  
  @media (min-width: 1024px) {
    font-size: 1.125rem;
    margin-bottom: 1.5rem;
  }
  
  li {
    margin-bottom: 0.75rem;
  }
`;

const Privacy = () => {
  const regionalSeo = useRegionalSeo('/privacy');

  return (
    <PageWrapper>
      <SEO
        title="Privacy Policy"
        description="Bloomwik privacy policy — how we collect, use, and protect your information."
        url="/privacy"
        language={regionalSeo.language}
        availableLanguages={regionalSeo.availableLanguages}
        alternateUrls={regionalSeo.alternateUrls}
      />
      <Header />
      <Main>
        <Container>
          <Content>
            <PageTitle>Privacy Policy</PageTitle>
            <LastUpdated>Last updated: December 10, 2024</LastUpdated>

            <Section>
              <Paragraph>
                At Bloomwik, we take your privacy seriously. This Privacy Policy explains how we collect, use, 
                disclose, and safeguard your information when you visit our website.
              </Paragraph>
            </Section>

            <Section>
              <SectionTitle>Information We Collect</SectionTitle>
              <Paragraph>
                We may collect information about you in a variety of ways. The information we may collect includes:
              </Paragraph>
              <List>
                <li><strong>Personal Data:</strong> Name, email address, and other contact information you voluntarily provide when contacting us or subscribing to our newsletter.</li>
                <li><strong>Usage Data:</strong> Information about how you access and use our website, including your IP address, browser type, and pages visited.</li>
                <li><strong>Cookies:</strong> We use cookies to enhance your experience, analyze site usage, and assist in marketing efforts.</li>
              </List>
            </Section>

            <Section>
              <SectionTitle>How We Use Your Information</SectionTitle>
              <Paragraph>We use the information we collect to:</Paragraph>
              <List>
                <li>Provide, maintain, and improve our services</li>
                <li>Send you newsletters and updates (with your consent)</li>
                <li>Respond to your comments and questions</li>
                <li>Analyze usage patterns to improve user experience</li>
                <li>Detect and prevent fraud or abuse</li>
              </List>
            </Section>

            <Section>
              <SectionTitle>Data Protection</SectionTitle>
              <Paragraph>
                We implement appropriate technical and organizational security measures to protect your personal 
                information. However, no method of transmission over the Internet is 100% secure, and we cannot 
                guarantee absolute security.
              </Paragraph>
            </Section>

            <Section>
              <SectionTitle>Your Rights</SectionTitle>
              <Paragraph>You have the right to:</Paragraph>
              <List>
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Opt-out of marketing communications</li>
                <li>Withdraw consent at any time</li>
              </List>
            </Section>

            <Section>
              <SectionTitle>Third-Party Services</SectionTitle>
              <Paragraph>
                Our website may contain links to third-party websites. We are not responsible for the privacy 
                practices of these external sites. We encourage you to review their privacy policies.
              </Paragraph>
            </Section>

            <Section>
              <SectionTitle>Changes to This Policy</SectionTitle>
              <Paragraph>
                We may update this Privacy Policy from time to time. We will notify you of any changes by 
                posting the new Privacy Policy on this page and updating the "Last updated" date.
              </Paragraph>
            </Section>

            <Section>
              <SectionTitle>Contact Us</SectionTitle>
              <Paragraph>
                If you have questions about this Privacy Policy, please contact us at hello@bloomwik.com.
              </Paragraph>
            </Section>
          </Content>
        </Container>
      </Main>
      <Footer />
    </PageWrapper>
  );
};

export default Privacy;

